// Constants and live formulas of the Products tab (docs/schema-v2.md §5.0b). The server recomputes everything it
// returns; these functions only give the taster an immediate result while filling the form.

export const PRODUCT_CATEGORIES = ['coffee', 'tea', 'oil', 'vinegar'];

// ── Product (catalog row) fields shared by the four sheet types ──
export const PRODUCT_INITIAL = { product_name: '', producer: '', origin: '', variety: '', harvest_year: '' };

// Inline product keys of a sheet payload ('' -> null, year as integer)
export function productPayload(form) {
  const text = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);
  const year = parseInt(form.harvest_year, 10);
  return {
    product_name: String(form.product_name || '').trim(), producer: text(form.producer), origin: text(form.origin),
    variety: text(form.variety), harvest_year: Number.isNaN(year) ? null : year,
  };
}

const isNum = (v) => v !== null && v !== undefined && v !== '' && !Number.isNaN(Number(v));
const num = (v) => (isNum(v) ? Number(v) : 0);
const round2 = (v) => Math.round(v * 100) / 100;

// ── Coffee: SCA 2004 cupping form ──
export const COFFEE_SCORED = ['fragrance_aroma', 'flavor', 'aftertaste', 'acidity', 'body', 'balance', 'overall'];
export const COFFEE_CUPS = ['uniformity_cups', 'clean_cup_cups', 'sweetness_cups'];
export const ROAST_LEVELS = ['light', 'medium_light', 'medium', 'medium_dark', 'dark'];

// Total = sum of the 7 attributes + 2 points per good cup (uniformity, clean cup, sweetness) − defects
// (taint 2 points, fault 4 points, per cup). null until the 7 attributes are all scored.
export function coffeeResult(f) {
  const scored = COFFEE_SCORED.filter((k) => isNum(f[k])).map((k) => Number(f[k]));
  const attributes = scored.reduce((a, b) => a + b, 0);
  const cups = 2 * COFFEE_CUPS.reduce((sum, k) => sum + num(f[k]), 0);
  const defects = 2 * num(f.taint_cups) + 4 * num(f.fault_cups);
  const complete = scored.length === COFFEE_SCORED.length;
  const total = complete ? round2(attributes + cups - defects) : null;
  return { attributes: round2(attributes), cups, defects, scoredCount: scored.length, total, qualityClass: coffeeClass(total) };
}

export function coffeeClass(total) {
  if (total === null || total === undefined || Number.isNaN(Number(total))) return null;
  if (total >= 90) return 'outstanding';
  if (total >= 85) return 'excellent';
  if (total >= 80) return 'very_good';
  return 'below_specialty';
}

// ── Olive oil: IOC/COI profile sheet ──
export const OIL_DEFECTS = ['defect_fusty', 'defect_musty', 'defect_winey', 'defect_frostbitten', 'defect_rancid'];
// Rev. 11 list of other negative attributes (suggestions: free text is accepted too)
export const OIL_OTHER_DEFECTS = ['heated_burnt', 'dry_hay', 'rough', 'greasy', 'vegetable_water', 'brine', 'metallic', 'esparto', 'grubby', 'cucumber'];
export const OIL_POSITIVES = ['fruity', 'bitter', 'pungent'];

export function oilBand(value) {
  const v = num(value);
  if (v <= 3) return 'delicate';
  if (v <= 6) return 'medium';
  return 'robust';
}

// Classification from ONE taster's marks: indicative only (the official one is the median of a panel of 8–12 tasters).
export function oilResult(f) {
  const maxDefect = Math.max(...OIL_DEFECTS.map((k) => num(f[k])), num(f.defect_other_intensity));
  const fruity = num(f.fruity);
  const bitter = num(f.bitter);
  const pungent = num(f.pungent);
  let categoryEu = 'lampante';
  if (fruity > 0 && maxDefect === 0) categoryEu = 'extra_virgin';
  else if (fruity > 0 && maxDefect <= 3.5) categoryEu = 'virgin';
  let categoryIoc = 'lampante';
  if (maxDefect > 6) categoryIoc = 'lampante';
  else if (maxDefect > 3.5 || fruity === 0) categoryIoc = 'ordinary_virgin';
  else categoryIoc = maxDefect === 0 ? 'extra_virgin' : 'virgin';
  return {
    maxDefect, categoryEu, categoryIoc,
    bands: { fruity: oilBand(fruity), bitter: oilBand(bitter), pungent: oilBand(pungent) },
    wellBalanced: bitter <= fruity + 2 && pungent <= fruity + 2,
    mild: bitter <= 2 && pungent <= 2,
  };
}

// ── Generic detail view configuration (labels: products.field.<name>, codes: products.<codeType>.<value>) ──
export const SHEET_DETAIL = {
  coffee: {
    path: 'coffee',
    codes: [['roast_level', 'roast']],
    groups: [
      ['attributes', COFFEE_SCORED.map((k) => [k, 0])],
      ['cups', COFFEE_CUPS.map((k) => [k, 5]).concat([['taint_cups', 5], ['fault_cups', 5]])],
    ],
  },
  oil: {
    path: 'oil',
    codes: [['fruity_type', 'fruity_type']],
    groups: [
      ['defects', OIL_DEFECTS.map((k) => [k, 10]).concat([['defect_other_intensity', 10]])],
      ['positives', OIL_POSITIVES.map((k) => [k, 10])],
    ],
  },
  tea: {
    path: 'tea',
    codes: [['tea_type', 'tea_type'], ['gbt_category', 'gbt_category']],
    groups: [
      ['brewing', [['leaf_grams', 0], ['water_ml', 0], ['water_temperature', 0], ['steep_seconds', 0]]],
      ['ratings', ['dry_leaf', 'liquor_colour', 'aroma', 'taste', 'infused_leaf', 'overall'].map((k) => [k, 10])],
    ],
    total: 50,
  },
  // research_profile protocol: Giudici–Lemmetti questionnaire, 14 perceived-intensity marks (0–10), no total score.
  // abtm_dop protocol: rendered from abtmSheet.js by ProductSheetDetail
  vinegar: {
    path: 'vinegar',
    codes: [['protocol', 'protocol'], ['vinegar_kind', 'vinegar_kind']],
    groups: [
      ['visual', ['visual_viscosity', 'visual_colour', 'visual_clearness', 'visual_gloss'].map((k) => [k, 10])],
      ['olfactory', ['olfactory_pungency', 'olfactory_persistence', 'olfactory_overall'].map((k) => [k, 10])],
      ['gustatory', ['gustatory_consistency', 'gustatory_sweetness', 'gustatory_acidity_intensity', 'gustatory_acidity_persistence',
        'gustatory_astringency', 'gustatory_saltiness', 'gustatory_bitterness'].map((k) => [k, 10])],
      ['final', [['overall', 10], ['declared_age_years', 0]]],
    ],
  },
};
